import { supabase } from './supabase';
import type {
  Pathway,
  PathwayWithStats,
  PathwayStage,
  PathwayAsset,
  PathwayTask,
  PathwayDetail,
  StageDetail,
  PathwayAssignment,
  PathwayProgress,
  PathwayTaskResponse,
  AssignedPathway,
  AssetType,
  TaskType,
  PathwayCreatorType,
  PathwayAssigneeType,
} from '../types/pathway';

function mapPathway(row: any): Pathway {
  return {
    id: row.id,
    coachId: row.coach_id,
    name: row.name,
    description: row.description,
    targetProfiles: row.target_profiles,
    isPublished: row.is_published,
    sequentialUnlock: row.sequential_unlock ?? true,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    creatorUserId: row.creator_user_id || null,
    creatorType: row.creator_type || 'coach',
  };
}

function mapStage(row: any): PathwayStage {
  return {
    id: row.id,
    pathwayId: row.pathway_id,
    position: row.position,
    title: row.title,
    objective: row.objective,
    description: row.description,
  };
}

function mapAsset(row: any): PathwayAsset {
  return {
    id: row.id,
    stageId: row.stage_id,
    type: row.type as AssetType,
    title: row.title,
    url: row.url,
    storagePath: row.storage_path,
    position: row.position,
  };
}

function mapTask(row: any): PathwayTask {
  return {
    id: row.id,
    stageId: row.stage_id,
    type: row.type as TaskType,
    prompt: row.prompt,
    position: row.position,
  };
}

function mapProgress(row: any): PathwayProgress {
  return {
    id: row.id,
    assignmentId: row.assignment_id,
    stageId: row.stage_id,
    status: row.status,
    startedAt: row.started_at,
    completedAt: row.completed_at,
  };
}

function mapTaskResponse(row: any): PathwayTaskResponse {
  return {
    id: row.id,
    progressId: row.progress_id,
    taskId: row.task_id,
    response: row.response,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ============================================================
// COACH: Pathway CRUD
// ============================================================

export async function fetchCoachPathways(coachId: string): Promise<PathwayWithStats[]> {
  const { data: pathways } = await supabase
    .from('pathways')
    .select('*')
    .eq('coach_id', coachId)
    .order('created_at', { ascending: false });

  if (!pathways || pathways.length === 0) return [];

  const pathwayIds = pathways.map(p => p.id);

  const [stagesRes, assignmentsRes] = await Promise.all([
    supabase
      .from('pathway_stages')
      .select('pathway_id')
      .in('pathway_id', pathwayIds),
    supabase
      .from('pathway_assignments')
      .select('pathway_id')
      .in('pathway_id', pathwayIds),
  ]);

  const stageCounts = new Map<string, number>();
  (stagesRes.data || []).forEach(s => {
    stageCounts.set(s.pathway_id, (stageCounts.get(s.pathway_id) || 0) + 1);
  });

  const assignmentCounts = new Map<string, number>();
  (assignmentsRes.data || []).forEach(a => {
    assignmentCounts.set(a.pathway_id, (assignmentCounts.get(a.pathway_id) || 0) + 1);
  });

  return pathways.map(row => ({
    ...mapPathway(row),
    stageCount: stageCounts.get(row.id) || 0,
    assignmentCount: assignmentCounts.get(row.id) || 0,
  }));
}

export async function createPathway(coachId: string, data: { name: string; description: string; targetProfiles: string; sequentialUnlock?: boolean }, creatorType: PathwayCreatorType = 'coach', creatorUserId?: string): Promise<string> {
  const insertData: any = {
    coach_id: coachId,
    name: data.name,
    description: data.description,
    target_profiles: data.targetProfiles,
    creator_type: creatorType,
    sequential_unlock: data.sequentialUnlock ?? true,
  };
  if (creatorUserId) insertData.creator_user_id = creatorUserId;

  const { data: row, error } = await supabase
    .from('pathways')
    .insert(insertData)
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function updatePathway(pathwayId: string, data: Partial<{ name: string; description: string; targetProfiles: string; isPublished: boolean; sequentialUnlock: boolean }>): Promise<void> {
  const payload: any = { updated_at: new Date().toISOString() };
  if (data.name !== undefined) payload.name = data.name;
  if (data.description !== undefined) payload.description = data.description;
  if (data.targetProfiles !== undefined) payload.target_profiles = data.targetProfiles;
  if (data.isPublished !== undefined) payload.is_published = data.isPublished;
  if (data.sequentialUnlock !== undefined) payload.sequential_unlock = data.sequentialUnlock;

  const { error } = await supabase
    .from('pathways')
    .update(payload)
    .eq('id', pathwayId);

  if (error) throw error;
}

export async function deletePathway(pathwayId: string): Promise<void> {
  const { error } = await supabase
    .from('pathways')
    .delete()
    .eq('id', pathwayId);

  if (error) throw error;
}

// ============================================================
// COACH: Pathway Detail (with stages, assets, tasks)
// ============================================================

export async function fetchPathwayDetail(pathwayId: string): Promise<PathwayDetail | null> {
  const { data: pathwayRow } = await supabase
    .from('pathways')
    .select('*')
    .eq('id', pathwayId)
    .maybeSingle();

  if (!pathwayRow) return null;

  const { data: stageRows } = await supabase
    .from('pathway_stages')
    .select('*')
    .eq('pathway_id', pathwayId)
    .order('position');

  const stages = (stageRows || []).map(mapStage);
  const stageIds = stages.map(s => s.id);

  if (stageIds.length === 0) {
    return { ...mapPathway(pathwayRow), stages: [] };
  }

  const [assetsRes, tasksRes] = await Promise.all([
    supabase
      .from('pathway_assets')
      .select('*')
      .in('stage_id', stageIds)
      .order('position'),
    supabase
      .from('pathway_tasks')
      .select('*')
      .in('stage_id', stageIds)
      .order('position'),
  ]);

  const assetsByStage = new Map<string, PathwayAsset[]>();
  (assetsRes.data || []).forEach(row => {
    const asset = mapAsset(row);
    const list = assetsByStage.get(asset.stageId) || [];
    list.push(asset);
    assetsByStage.set(asset.stageId, list);
  });

  const tasksByStage = new Map<string, PathwayTask[]>();
  (tasksRes.data || []).forEach(row => {
    const task = mapTask(row);
    const list = tasksByStage.get(task.stageId) || [];
    list.push(task);
    tasksByStage.set(task.stageId, list);
  });

  const stageDetails: StageDetail[] = stages.map(stage => ({
    ...stage,
    assets: assetsByStage.get(stage.id) || [],
    tasks: tasksByStage.get(stage.id) || [],
  }));

  return { ...mapPathway(pathwayRow), stages: stageDetails };
}

// ============================================================
// COACH: Stage CRUD
// ============================================================

export async function addStage(pathwayId: string, position: number, data: { title: string; objective: string }): Promise<string> {
  const { data: row, error } = await supabase
    .from('pathway_stages')
    .insert({
      pathway_id: pathwayId,
      position,
      title: data.title,
      objective: data.objective,
    })
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function updateStage(stageId: string, data: Partial<{ title: string; objective: string; description: string; position: number }>): Promise<void> {
  const payload: any = { updated_at: new Date().toISOString() };
  if (data.title !== undefined) payload.title = data.title;
  if (data.objective !== undefined) payload.objective = data.objective;
  if (data.description !== undefined) payload.description = data.description;
  if (data.position !== undefined) payload.position = data.position;

  const { error } = await supabase
    .from('pathway_stages')
    .update(payload)
    .eq('id', stageId);

  if (error) throw error;
}

export async function deleteStage(stageId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_stages')
    .delete()
    .eq('id', stageId);

  if (error) throw error;
}

// ============================================================
// COACH: Asset CRUD
// ============================================================

export async function addAsset(stageId: string, data: { type: AssetType; title: string; url?: string; storagePath?: string; position: number }): Promise<string> {
  if (data.type === 'video_embed' && data.url && !data.storagePath) {
    const { data: rpcId, error: rpcError } = await supabase
      .rpc('add_pathway_video_asset', {
        p_stage_id: stageId,
        p_title: data.title,
        p_url: data.url,
      });

    if (rpcError) throw rpcError;
    return rpcId as string;
  }

  const { data: row, error } = await supabase
    .from('pathway_assets')
    .insert({
      stage_id: stageId,
      type: data.type,
      title: data.title,
      url: data.url || '',
      storage_path: data.storagePath || '',
      position: data.position,
    })
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function deleteAsset(assetId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_assets')
    .delete()
    .eq('id', assetId);

  if (error) throw error;
}

export async function uploadAssetFile(coachId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'bin';
  const path = `${coachId}/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('pathway-assets')
    .upload(path, file);

  if (error) throw error;
  return path;
}

export function getAssetPublicUrl(storagePath: string): string {
  const { data } = supabase.storage
    .from('pathway-assets')
    .getPublicUrl(storagePath);
  return data.publicUrl;
}

export async function getAssetSignedUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('pathway-assets')
    .createSignedUrl(storagePath, 3600);

  if (error) throw error;
  return data.signedUrl;
}

export async function deleteAssetFile(storagePath: string): Promise<void> {
  const { error } = await supabase.storage
    .from('pathway-assets')
    .remove([storagePath]);

  if (error) throw error;
}

// ============================================================
// COACH: Task CRUD
// ============================================================

export async function addTask(stageId: string, data: { type: TaskType; prompt: string; position: number }): Promise<string> {
  const { data: row, error } = await supabase
    .from('pathway_tasks')
    .insert({
      stage_id: stageId,
      type: data.type,
      prompt: data.prompt,
      position: data.position,
    })
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function updateTask(taskId: string, data: { prompt: string }): Promise<void> {
  const { error } = await supabase
    .from('pathway_tasks')
    .update({ prompt: data.prompt })
    .eq('id', taskId);

  if (error) throw error;
}

export async function deleteTask(taskId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_tasks')
    .delete()
    .eq('id', taskId);

  if (error) throw error;
}

// ============================================================
// COACH: Assignment management
// ============================================================

export async function fetchPathwayAssignments(pathwayId: string): Promise<{ respondentId: string; respondentName: string; respondentEmail: string; assignedAt: string; completedAt: string | null }[]> {
  const { data: assignments } = await supabase
    .from('pathway_assignments')
    .select('respondent_id, assigned_at, completed_at')
    .eq('pathway_id', pathwayId);

  if (!assignments || assignments.length === 0) return [];

  const respondentIds = assignments.map(a => a.respondent_id).filter(Boolean) as string[];
  const { data: respondents } = await supabase
    .from('respondents')
    .select('id, users!respondents_user_id_fkey(full_name, email)')
    .in('id', respondentIds);

  const respMap = new Map<string, { name: string; email: string }>();
  (respondents || []).forEach(r => {
    respMap.set(r.id, {
      name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
      email: (r.users as any)?.email || '',
    });
  });

  return assignments.map(a => {
    const rid = a.respondent_id || '';
    const info = respMap.get(rid) || { name: 'Unknown', email: '' };
    return {
      respondentId: rid,
      respondentName: info.name,
      respondentEmail: info.email,
      assignedAt: a.assigned_at,
      completedAt: a.completed_at,
    };
  });
}

export async function assignPathway(pathwayId: string, respondentId: string, coachId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_assignments')
    .insert({
      pathway_id: pathwayId,
      respondent_id: respondentId,
      coach_id: coachId,
    });

  if (error) throw error;
}

export async function unassignPathway(pathwayId: string, respondentId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_assignments')
    .delete()
    .eq('pathway_id', pathwayId)
    .eq('respondent_id', respondentId);

  if (error) throw error;
}

export async function fetchCoachRespondentsForAssignment(coachId: string): Promise<{ id: string; name: string; email: string }[]> {
  const { data } = await supabase
    .from('respondents')
    .select('id, users!respondents_user_id_fkey(full_name, email)')
    .eq('coach_id', coachId);

  return (data || []).map(r => ({
    id: r.id,
    name: (r.users as any)?.full_name || (r.users as any)?.email || 'Unknown',
    email: (r.users as any)?.email || '',
  }));
}

// ============================================================
// PARTNER: Pathway CRUD
// ============================================================

export async function fetchPartnerPathways(partnerUserId: string): Promise<PathwayWithStats[]> {
  const { data: pathways } = await supabase
    .from('pathways')
    .select('*')
    .eq('creator_type', 'partner')
    .eq('creator_user_id', partnerUserId)
    .order('created_at', { ascending: false });

  if (!pathways || pathways.length === 0) return [];

  const pathwayIds = pathways.map(p => p.id);

  const [stagesRes, assignmentsRes] = await Promise.all([
    supabase
      .from('pathway_stages')
      .select('pathway_id')
      .in('pathway_id', pathwayIds),
    supabase
      .from('pathway_assignments')
      .select('pathway_id')
      .in('pathway_id', pathwayIds),
  ]);

  const stageCounts = new Map<string, number>();
  (stagesRes.data || []).forEach(s => {
    stageCounts.set(s.pathway_id, (stageCounts.get(s.pathway_id) || 0) + 1);
  });

  const assignmentCounts = new Map<string, number>();
  (assignmentsRes.data || []).forEach(a => {
    assignmentCounts.set(a.pathway_id, (assignmentCounts.get(a.pathway_id) || 0) + 1);
  });

  return pathways.map(row => ({
    ...mapPathway(row),
    stageCount: stageCounts.get(row.id) || 0,
    assignmentCount: assignmentCounts.get(row.id) || 0,
  }));
}

export async function createPartnerPathway(partnerUserId: string, data: { name: string; description: string; targetProfiles: string; sequentialUnlock?: boolean }): Promise<string> {
  const { data: row, error } = await supabase
    .from('pathways')
    .insert({
      name: data.name,
      description: data.description,
      target_profiles: data.targetProfiles,
      creator_type: 'partner',
      creator_user_id: partnerUserId,
      coach_id: null as any,
      sequential_unlock: data.sequentialUnlock ?? true,
    })
    .select('id')
    .single();

  if (error) throw error;
  return row.id;
}

export async function fetchPartnerCoachesForPathwayAssignment(partnerUserId: string): Promise<{ userId: string; name: string; email: string }[]> {
  const { data: partner } = await supabase
    .from('partners')
    .select('id')
    .eq('user_id', partnerUserId)
    .maybeSingle();

  if (!partner) return [];

  const { data: coaches } = await supabase
    .from('coaches')
    .select('user_id')
    .eq('partner_id', partner.id);

  if (!coaches || coaches.length === 0) return [];

  const userIds = coaches.map(c => c.user_id);
  const { data: users } = await supabase
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  return (users || []).map(u => ({
    userId: u.id,
    name: u.full_name || u.email || 'Unknown',
    email: u.email || '',
  }));
}

export async function assignPathwayToCoach(pathwayId: string, coachUserId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_assignments')
    .insert({
      pathway_id: pathwayId,
      assignee_user_id: coachUserId,
      assignee_type: 'coach',
    });

  if (error) throw error;
}

export async function unassignPathwayFromCoach(pathwayId: string, coachUserId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_assignments')
    .delete()
    .eq('pathway_id', pathwayId)
    .eq('assignee_user_id', coachUserId)
    .eq('assignee_type', 'coach');

  if (error) throw error;
}

export async function fetchPathwayCoachAssignments(pathwayId: string): Promise<{ assigneeUserId: string; assigneeName: string; assigneeEmail: string; assignedAt: string; completedAt: string | null }[]> {
  const { data: assignments } = await supabase
    .from('pathway_assignments')
    .select('assignee_user_id, assigned_at, completed_at')
    .eq('pathway_id', pathwayId)
    .eq('assignee_type', 'coach');

  if (!assignments || assignments.length === 0) return [];

  const userIds = assignments.map(a => a.assignee_user_id).filter(Boolean) as string[];
  const { data: users } = await supabase
    .from('users')
    .select('id, full_name, email')
    .in('id', userIds);

  const userMap = new Map<string, { name: string; email: string }>();
  (users || []).forEach(u => {
    userMap.set(u.id, { name: u.full_name || u.email || 'Unknown', email: u.email || '' });
  });

  return assignments.map(a => {
    const uid = a.assignee_user_id || '';
    const info = userMap.get(uid) || { name: 'Unknown', email: '' };
    return {
      assigneeUserId: uid,
      assigneeName: info.name,
      assigneeEmail: info.email,
      assignedAt: a.assigned_at,
      completedAt: a.completed_at,
    };
  });
}

// ============================================================
// RESPONDENT: Pathway views
// ============================================================

export async function fetchMyPathways(respondentId: string): Promise<AssignedPathway[]> {
  return fetchAssignedPathways(respondentId, 'respondent');
}

// ============================================================
// COACH ASSIGNEE: Pathway views (pathways assigned to coach by partner)
// ============================================================

export async function fetchMyPathwaysAsCoach(coachUserId: string): Promise<AssignedPathway[]> {
  return fetchAssignedPathways(coachUserId, 'coach');
}

async function fetchAssignedPathways(assigneeUserId: string, assigneeType: PathwayAssigneeType): Promise<AssignedPathway[]> {
  const { data: assignments } = await supabase
    .from('pathway_assignments')
    .select('id, pathway_id, completed_at')
    .eq('assignee_user_id', assigneeUserId)
    .eq('assignee_type', assigneeType);

  if (!assignments || assignments.length === 0) return [];

  const pathwayIds = assignments.map(a => a.pathway_id);

  const [pathwaysRes, stagesRes, progressRes] = await Promise.all([
    supabase
      .from('pathways')
      .select('*')
      .in('id', pathwayIds)
      .eq('is_published', true),
    supabase
      .from('pathway_stages')
      .select('id, pathway_id, position, title')
      .in('pathway_id', pathwayIds)
      .order('position'),
    supabase
      .from('pathway_progress')
      .select('assignment_id, stage_id, status')
      .in('assignment_id', assignments.map(a => a.id)),
  ]);

  const pathwayMap = new Map<string, Pathway>();
  (pathwaysRes.data || []).forEach(row => {
    pathwayMap.set(row.id, mapPathway(row));
  });

  const stagesByPathway = new Map<string, { id: string; title: string; position: number }[]>();
  (stagesRes.data || []).forEach(s => {
    const list = stagesByPathway.get(s.pathway_id) || [];
    list.push({ id: s.id, title: s.title, position: s.position });
    stagesByPathway.set(s.pathway_id, list);
  });

  const completedByAssignment = new Map<string, Set<string>>();
  (progressRes.data || []).forEach(p => {
    if (p.status === 'completed') {
      const set = completedByAssignment.get(p.assignment_id) || new Set();
      set.add(p.stage_id);
      completedByAssignment.set(p.assignment_id, set);
    }
  });

  const result: AssignedPathway[] = [];
  for (const assignment of assignments) {
    const pathway = pathwayMap.get(assignment.pathway_id);
    if (!pathway) continue;

    const stages = stagesByPathway.get(assignment.pathway_id) || [];
    const completedSet = completedByAssignment.get(assignment.id) || new Set();
    const completedCount = stages.filter(s => completedSet.has(s.id)).length;

    const currentStage = stages.find(s => !completedSet.has(s.id));

    result.push({
      assignmentId: assignment.id,
      pathway,
      totalStages: stages.length,
      completedStages: completedCount,
      currentStageTitle: currentStage?.title || null,
    });
  }

  return result;
}

export async function fetchPathwayForRespondent(assignmentId: string): Promise<{
  assignment: PathwayAssignment;
  pathway: PathwayDetail;
  progress: PathwayProgress[];
  taskResponses: PathwayTaskResponse[];
} | null> {
  const { data: rpcData, error: rpcError } = await supabase
    .rpc('fetch_pathway_detail_for_assignee', { p_assignment_id: assignmentId });

  if (rpcError || !rpcData) return null;

  const raw = rpcData as any;
  const assignmentRow = raw.assignment;
  if (!assignmentRow) return null;

  const assignment: PathwayAssignment = {
    id: assignmentRow.id,
    pathwayId: assignmentRow.pathway_id,
    respondentId: assignmentRow.respondent_id || '',
    coachId: assignmentRow.coach_id || '',
    assignedAt: assignmentRow.assigned_at,
    completedAt: assignmentRow.completed_at,
    assigneeUserId: assignmentRow.assignee_user_id || null,
    assigneeType: (assignmentRow.assignee_type as PathwayAssigneeType) || 'respondent',
  };
  const pathwayRow = raw.pathway;
  const stageRows: any[] = raw.stages || [];
  const assetRows: any[] = raw.assets || [];
  const taskRows: any[] = raw.tasks || [];

  const stages = stageRows.map(mapStage);
  const assetsByStage = new Map<string, PathwayAsset[]>();
  assetRows.forEach(row => {
    const asset = mapAsset(row);
    const list = assetsByStage.get(asset.stageId) || [];
    list.push(asset);
    assetsByStage.set(asset.stageId, list);
  });
  const tasksByStage = new Map<string, PathwayTask[]>();
  taskRows.forEach(row => {
    const task = mapTask(row);
    const list = tasksByStage.get(task.stageId) || [];
    list.push(task);
    tasksByStage.set(task.stageId, list);
  });

  const stageDetails: StageDetail[] = stages.map(stage => ({
    ...stage,
    assets: assetsByStage.get(stage.id) || [],
    tasks: tasksByStage.get(stage.id) || [],
  }));

  const pathway: PathwayDetail = { ...mapPathway(pathwayRow), stages: stageDetails };
  const progress = ((raw.progress || []) as any[]).map(mapProgress);
  const taskResponses = ((raw.taskResponses || []) as any[]).map(mapTaskResponse);

  return { assignment, pathway, progress, taskResponses };
}

export async function upsertStageProgress(assignmentId: string, stageId: string, status: 'not_started' | 'in_progress' | 'completed'): Promise<string> {
  const { data: existing } = await supabase
    .from('pathway_progress')
    .select('id')
    .eq('assignment_id', assignmentId)
    .eq('stage_id', stageId)
    .maybeSingle();

  const now = new Date().toISOString();

  if (existing) {
    const payload: any = { status };
    if (status === 'in_progress' && !existing) payload.started_at = now;
    if (status === 'completed') payload.completed_at = now;

    const { error } = await supabase
      .from('pathway_progress')
      .update(payload)
      .eq('id', existing.id);

    if (error) throw error;
    return existing.id;
  } else {
    const payload: any = {
      assignment_id: assignmentId,
      stage_id: stageId,
      status,
    };
    if (status === 'in_progress') payload.started_at = now;
    if (status === 'completed') {
      payload.started_at = now;
      payload.completed_at = now;
    }

    const { data: row, error } = await supabase
      .from('pathway_progress')
      .insert(payload)
      .select('id')
      .single();

    if (error) throw error;
    return row.id;
  }
}

export async function upsertTaskResponse(progressId: string, taskId: string, response: string): Promise<void> {
  const { data: existing } = await supabase
    .from('pathway_task_responses')
    .select('id')
    .eq('progress_id', progressId)
    .eq('task_id', taskId)
    .maybeSingle();

  if (existing) {
    const { error } = await supabase
      .from('pathway_task_responses')
      .update({ response, updated_at: new Date().toISOString() })
      .eq('id', existing.id);

    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('pathway_task_responses')
      .insert({
        progress_id: progressId,
        task_id: taskId,
        response,
      });

    if (error) throw error;
  }
}

export async function markPathwayComplete(assignmentId: string): Promise<void> {
  const { error } = await supabase
    .from('pathway_assignments')
    .update({ completed_at: new Date().toISOString() })
    .eq('id', assignmentId);

  if (error) throw error;
}
